import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { combineLatest, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import { ApplicationService, AttachmentDeadlineInfo, NominationForm, NominationSaveFormData, NominationSaveFormPublic, NominationWindowInfo, NomineeTypeInfo } from '../../services/application/application.service';
import { DialogService } from '../../services/dialogs/dialogs.service';
import { ScrollService } from '../../services/scroll/scroll.service';
import { SettingsService } from '../../services/settings/settings.service';
import { SplashService } from '../../services/splash/splash.service';

@Component({
    selector: 'app-publicnominate',
    templateUrl: './publicnominate.component.html',
    styleUrls: ['./publicnominate.component.css']
})

export class PublicnominateComponent implements OnInit, OnDestroy {
    private _subrelease: Subject<void> = new Subject<void>();
    NomineeType: NomineeTypeInfo;
    NominationForm: FormGroup;
    FileWindowDeadline: AttachmentDeadlineInfo;
    UploadedPhoto: File;
    SelectedTabIndex: number;
    PhotoWindowSet: boolean;
    FormWindowOpen: boolean;
    ViewReady: boolean;
    AlreadySubmitted: boolean;
    CaptchaResponse: string;
    ShowSubmittedWarning: boolean;
    ShowCaptchaWarning: boolean;
    ShowFormWarning: boolean;
	ShowPhotoWarning: boolean;
	SchoolYear: string;

    constructor(private appSvc: ApplicationService,
		private settingsSvc: SettingsService,
        private dlgSvc: DialogService,
        private fb: FormBuilder,
        private router: Router,
        private scrollSvc: ScrollService,
        private splashSvc: SplashService) { }

    ngOnInit(): void {
        this.PhotoWindowSet = false;
        this.FormWindowOpen = false;
        this.ViewReady = false;
        this.AlreadySubmitted = false;
        this.ShowCaptchaWarning = false;
        this.ShowSubmittedWarning = false;
        this.ShowFormWarning = false;
        this.ShowPhotoWarning = false;
        this.SelectedTabIndex = 0;
        this.checkRequirements();
    }

    checkRequirements() {
        let url = this.router.url;
        let awardInitials = url.split('/')[1];

        //check if Hall of Fame //could check to see if nominations are public from flag in DB if more than one award is like this in the future
        if (awardInitials !== 'HOF') {
            return;
		}

		this.settingsSvc.CurrentSchoolYearSubject.pipe(takeUntil(this._subrelease)).subscribe(year => {
			this.SchoolYear = year;

			const WindowObs = this.appSvc.GetNominationWindows(year);
			const NomTypeObs = this.appSvc.GetNomineeTypes();

			combineLatest(WindowObs, NomTypeObs).pipe(
				takeUntil(this._subrelease),
				map(p => {
					return {
						Windows: p[0],
						NomTypes: p[1]
					};
				})
			).subscribe(data => {
				let NomWindows: NominationWindowInfo[] = data.Windows;
				let NomTypes: NomineeTypeInfo[] = data.NomTypes;
				this.NomineeType = NomTypes.find(f => f.ShortName === awardInitials);

				//find correct window using NomType, Year, and WindowType/CODE
				let formWindow = NomWindows.find(f => f.NomineeTypeID == this.NomineeType.NomineeTypeID && f.SchoolYear === year && f.WindowTypeCode === 'FORM');
				//check Date of window
				if (formWindow == null || formWindow.Open === false) {
					return;
				}

				//let photoWindow = NomWindows.find(f => f.NomineeTypeID == this.NomineeType.NomineeTypeID &&  f.SchoolYear === year && f.WindowTypeCode === 'PHOTO');
				//if (photoWindow == null || photoWindow.Open === false) {
				//    return;
				//}

				this.FileWindowDeadline = {
					WindowOpen: true
				};
				this.FormWindowOpen = true;
				this.PhotoWindowSet = true;
				this.getForm();
			});
		});
    }

    getForm() {
        //get FormTemplateID of NomTypeID with latest DateUpdated
        this.appSvc.GetNominationFormPublic(this.NomineeType.NomineeTypeID).pipe(takeUntil(this._subrelease)).subscribe(res => {
            const form = this.fb.group({
                NomineeTypeID: [res.NomineeTypeID, Validators.required],
                FormTemplateID: [res.FormTemplateID, Validators.required],
                FormInstructions: [res.FormInstructions],
                Sections: [],
                Photos: []
            });

            if (res.Sections && !!res.Sections.length) {
                const secs = <FormGroup[]>[];

                res.Sections.forEach(sec => {
                    const esec = this.fb.group({
                        SectionID: [sec.SectionID],
                        Name: [sec.Name],
                        RomanNumeral: [sec.RomanNumeral],
                        Instructions: [sec.Instructions],
                        SortOrder: [sec.SortOrder],
                        SectionReady: false,
                        IsAttachment: [sec.IsAttachment],
                        AttachmentType: [sec.AttachmentType],
                        PrincipalOnly: [sec.PrincipalOnly],
                        JudgingAvailable: [sec.JudgingAvailable],
                        Questions: [],
                    });

                    const qarr = <FormGroup[]>[];

                    (sec.Questions || []).forEach(q => {
                        const question = this.fb.group({
                            SectionID: [q.SectionID],
                            SectionDetailID: [q.SectionDetailID],
                            QuestionID: [q.QuestionID],
                            QuestionTypeID: [q.QuestionTypeID],
                            QuestionText: [q.QuestionText],
                            Text2: [q.Text2],
                            QuestionNarrativeID: [q.QuestionNarrativeID],
                            NarrativeText: [q.NarrativeText],
                            TextLimit: [q.TextLimit],
                            TextLimitUnit: [q.TextLimitUnit],
                            LowerTextLimit: [q.LowerTextLimit],
                            Response: null,
                            SortOrder: [q.SortOrder],
                            ShowNumber: [q.ShowNumber],
                            ShowNarrative: [q.ShowNarrative],
                            QuestionNumber: [q.QuestionNumber],
                            DigitOnly: [q.DigitOnly],
                            ResponseInvalid: [q.ResponseInvalid],
                            InitiallyBlank: [q.InitiallyBlank],
                            TextSizeCount: [q.TextSizeCount],
                            OtherResponse: [q.OtherResponse],
                            HasOptions: [q.HasOptions],
                            CanSelectMultiple: [q.CanSelectMultiple],
                            QuestionType: [q.QuestionType],
                            Options: [q.Options],
                            OtherSelected: [q.OtherSelected],
                            FirstName: [q.FirstName],
                            LastName: [q.LastName],
                            Inline: [q.Inline]
                        });

                        if (q.QuestionType === 'ShortResponse') {
                            question.get('Response').valueChanges.pipe(
                                takeUntil(this._subrelease),
                                distinctUntilChanged(),
                                debounceTime(250)
                            ).subscribe(value => {
                                if (question.get('DigitOnly').value) {
                                    if (/^\d+$/.test(value)) {
                                        question.get('ResponseInvalid').setValue(false, { emitEvent: false });
                                    }
                                    else {
                                        question.get('ResponseInvalid').setValue(true, { emitEvent: false });
                                    }
                                }

                                this.checkSectionReady(esec);
                            });
                        }

                        if (q.QuestionType === 'LongResponse') {
                            // This is a text-entry (paragraph) based entry. For those, we also subscribe to the value of the
                            // response entry, so we can update the word counts for the overall formgroup.
                            question.get('Response').valueChanges.pipe(
                                takeUntil(this._subrelease),
                                distinctUntilChanged(),
                                debounceTime(250)
                            ).subscribe(value => {
                                const qval = question.getRawValue();

                                // No need to update counts if there's no limit defined.
                                if (qval.TextLimit == null) {
                                    return;
                                }

                                // Depending on the unit (1 = letters, 2 = words), calculate the count now.
                                const text = value || '';

                                let unitcount: number;
                                if (text == null || text.trim() == '') {
                                    unitcount = 0;
                                }
                                else if (qval.TextLimitUnit === 1) {
                                    unitcount = text.length;
                                }
                                else {
                                    unitcount = text.match(/\S+/g).length;
                                }

                                // Update the count in the group
                                question.get('TextSizeCount').setValue(unitcount, { emitEvent: false });

                                this.checkSectionReady(esec);
                            });
                        }

                        if (q.QuestionType === 'YesNo') {
                            question.get('Response').valueChanges.pipe(
                                takeUntil(this._subrelease),
                                distinctUntilChanged(),
                                debounceTime(250)
                            ).subscribe(value => {
                                this.checkSectionReady(esec);
                            });
                        }

                        if (q.QuestionType === 'SingleCheckbox') {
                            question.get('Response').valueChanges.pipe(
                                takeUntil(this._subrelease),
                                distinctUntilChanged(),
                                debounceTime(250)
                            ).subscribe(value => {
                                this.checkSectionReady(esec);
                            });
                        }

                        if (q.Response == null || q.Response == "" || q.Response === 'This response has already been provided by your supervisor' || q.Response === 'Your supervisor will provide this response') {
                            question.get('InitiallyBlank').setValue(true, { emitEvent: false });
                        }

                        if (q.HasOptions) {
                            if (q.CanSelectMultiple) {
                                //value change event
                                question.get('Response').valueChanges.pipe(
                                    takeUntil(this._subrelease),
                                    distinctUntilChanged(),
                                    debounceTime(250)
                                ).subscribe((value: number[]) => {
                                    //find the other option, can only have one other per question unless change db or use separators like in response
                                    let other = q.Options.find(o => o.RequiresNote == true);

                                    //if values include other value
                                    if (other && value.includes(other.Value)) {
                                        question.get('OtherSelected').setValue(true, { emitEvent: false });
                                        q.OtherSelected = true;
                                    }
                                    else {
                                        //if not selected, set OtherSelected to false and clear OtherResponse
                                        question.get('OtherSelected').setValue(false, { emitEvent: false });
                                        q.OtherSelected = false;
                                        question.get('OtherResponse').setValue(null, { emitEvent: false });
                                        q.OtherResponse = null;
                                    }

                                    this.checkSectionReady(esec);
                                });

                                if (q.Response != null) {
                                    let selectedOptions = q.Response.indexOf('|') < 0 ? [+q.Response] : q.Response.split('|').map(x => +x);

                                    question.get('Response').setValue(selectedOptions, { emitEvent: false });
                                }
                            }
                            else {
                                //value change event
                                question.get('Response').valueChanges.pipe(
                                    takeUntil(this._subrelease),
                                    distinctUntilChanged(),
                                    debounceTime(250)
                                ).subscribe((value: number) => {
                                    //find the other option, can only have one other per question unless change db or use separators like in response
                                    let other = q.Options.find(o => o.RequiresNote == true);

                                    //if value = other, set OtherSelected to true
                                    if (other && other.Value == value) {
                                        question.get('OtherSelected').setValue(true, { emitEvent: false });
                                        q.OtherSelected = true;
                                    }
                                    else {
                                        //if not selected, set OtherSelected to false and clear OtherResponse
                                        question.get('OtherSelected').setValue(false, { emitEvent: false });
                                        q.OtherSelected = false;
                                        question.get('OtherResponse').setValue(null, { emitEvent: false });
                                        q.OtherResponse = null;
                                    }

                                    this.checkSectionReady(esec);
                                });

                                //convert string to number
                                question.get('Response').setValue(+q.Response, { emitEvent: false });
                            }
                        }

                        qarr.push(question);
                    }); //end question for loop

                    esec.setControl('Questions', this.fb.array(qarr));
                    secs.push(esec);
                }); //end section for loop

                form.setControl('Sections', this.fb.array(secs));
            }

            this.NominationForm = form;
            this.ViewReady = true;
        }); //end get form observable
    }

    ChangeTab(index: number) {
        this.SelectedTabIndex = index;

        // Trigger the scroll event so the master container can scroll the panel back up to view.
        this.scrollSvc.TriggerScroll();
    }

    checkSectionReady(section: FormGroup) {
        //check if every question is answered
        if (section.value.Questions.every(e => e.Response != null && e.Response != '')) {
            section.get('SectionReady').setValue(true, { emitEvent: false });
        }
        else {
            section.get('SectionReady').setValue(false, { emitEvent: false });
        }
    }

    GetAttachment(file: File) {
        let sections = <FormArray>this.NominationForm.get('Sections');
        let secInQuestion = sections.at(this.SelectedTabIndex);
        secInQuestion.get('SectionReady').setValue(true, { emitEvent: false });
        this.UploadedPhoto = file;
    }

    SaveForm() {
        //check if all sections are ready
        if (this.NominationForm.value.Sections.some(e => e.SectionReady === false)) {
            this.ShowFormWarning = true;
            return;
        }
        this.ShowFormWarning = false;

        //if (this.UploadedPhoto == null) {
        //    this.ShowPhotoWarning = true;
        //}
        //this.ShowPhotoWarning = false;

        if (this.CaptchaResponse == null) {
            this.ShowCaptchaWarning = true;
            return;
        }
        this.ShowCaptchaWarning = false;

        if (this.AlreadySubmitted === true) {
            this.ShowSubmittedWarning = true;
            return;
        }
        this.AlreadySubmitted = true;

        const formData: NominationSaveFormData[] = [];
        const form = <NominationForm>this.NominationForm.getRawValue();

        for (let i = 0, j = form.Sections.length; i < j; i++) {
            const sec = form.Sections[i];

            if (sec.IsAttachment) {
                continue; // There is nothing to save for attachment sections. Those are handled separately.
            }

            // Iterate through all questions in this section.
            for (let qi = 0, qj = sec.Questions.length; qi < qj; ++qi) {
                let quest = sec.Questions[qi];

                //is it an array (multiple options selected), or a single selection list (value), or just a string
                var response;
                if (Array.isArray(quest.Response)) {
                    response = quest.Response.join('|');
                }
                else if (quest.Response && quest.Response.Value) {
                    response = quest.Response.Value;
                }
                else {
                    response = quest.Response;
                }

                // Package up the FormTemplateSectionID, QuestionID, and response value.
                const answer: NominationSaveFormData = {
                    FormTemplateSectionID: quest.SectionID,
                    FormTemplateSectionDetailID: quest.SectionDetailID,
                    QuestionID: quest.QuestionID,
                    Response: response,
                    OtherResponse: quest.OtherResponse
                };

                //don't send blank answers
                if (response == null || response == "" || response === 'This response has already been provided by your supervisor' || response === 'Your supervisor will provide this response') {
                    continue;
                }

                if (quest.TextLimit != null) {
                    // There's a limit. Compare the size to the limit, and see if we are in violation.
                    if (quest.TextSizeCount > quest.TextLimit) {
                        // The violation is real. Warn the user, and abort the save operation.
                        const message =
                            `A text response to a question on this form is over the required text limit. ` +
                            `Please review the response size for the question shown below and revise the response to fit ` +
                            `within the required text length before saving.<br /><br /><strong>Section: </strong>` +
                            `${sec.Name}<br /><br /><strong>Question: </strong><em>${quest.QuestionText}</em>`;

                        this.dlgSvc.OpenMessageBoxDialog({
                            TitleText: 'Response Too Long',
                            MessageText: message,
                            OKText: 'OK',
                        }).pipe(takeUntil(this._subrelease)).subscribe(() => {
                            // Be kind and move the tab focus to the tab that has the issue.
                            this.ChangeTab(i);
                        });

                        this.AlreadySubmitted = false;
                        return;
                    }
                }

                if (quest.LowerTextLimit != null) {
                    // There's a limit. Compare the size to the limit, and see if we are in violation.
                    if (quest.TextSizeCount > 0 && quest.TextSizeCount < quest.LowerTextLimit) {
                        // The violation is real. Warn the user, and abort the save operation.

                        var message = 'A text response to a question on this form is under the required text limit. Please review the response size for the question shown below and revise the response to fit within the required text length before saving.<br /><br />';

                        message += '<strong>Section: </strong>' + sec.Name + '<br /><br /><strong>Question: </strong><em>' + quest.QuestionText + '</em>';

                        this.dlgSvc.OpenMessageBoxDialog({
                            TitleText: 'Response Too Short',
                            MessageText: message,
                            OKText: 'OK'
                        }).pipe(takeUntil(this._subrelease)).subscribe(() => {
                            // Be kind and move the tab focus to the tab that has the issue.
                            this.ChangeTab(i);
                        });

                        this.AlreadySubmitted = false;
                        return;
                    }
                }

                if (quest.DigitOnly) {
                    // There's a validator. Compare and see if we are in violation.
                    if (!/^\d+$/.test(response)) {
                        // The violation is real. Warn the user, and abort the save operation.

                        var message = 'This question requires the answer to only contain numbers. Please review the question and revise your answer to fit the requirements.<br /><br />';

                        message += '<strong>Section: </strong>' + sec.Name + '<br /><br /><strong>Question: </strong><em>' + quest.QuestionText + '</em>' + '<br /><br /><strong>Your Response: </strong><em>' + quest.Response + '</em>';

                        this.dlgSvc.OpenMessageBoxDialog({
                            TitleText: 'Invalid Response',
                            MessageText: message,
                            OKText: 'OK'
                        }).pipe(takeUntil(this._subrelease)).subscribe(() => {
                            // Be kind and move the tab focus to the tab that has the issue.
                            this.ChangeTab(i);
                        });

                        this.AlreadySubmitted = false;
                        return;
                    }
                }

                if (quest.OtherSelected && quest.OtherResponse == null) {
                    var message = 'You selected a custom response for this question but did not provide your own response. Please review the question and revise your answer to fit the requirements.<br /><br />';

                    message += '<strong>Section: </strong>' + sec.Name + '<br /><br /><strong>Question: </strong><em>' + quest.QuestionText + '</em>' + '<br /><br /><strong>Your Response: </strong><em>' + quest.Options.find(o => o.RequiresNote == true).DisplayText + '</em>' + '<br /><br /><strong>Custom Response: </strong><em>' + quest.OtherResponse + '</em>';

                    this.dlgSvc.OpenMessageBoxDialog({
                        TitleText: 'Custom Response Not Provided',
                        MessageText: message,
                        OKText: 'OK'
                    }).pipe(takeUntil(this._subrelease)).subscribe(() => {
                        // Be kind and move the tab focus to the tab that has the issue.
                        this.ChangeTab(i);
                    });

                    this.AlreadySubmitted = false;
                    return;
                }

                // Add to the form data array to be saved.
                formData.push(answer);
            }
        }

        let packet: NominationSaveFormPublic = {
            Photo: this.UploadedPhoto,
            Form: formData,
			Captcha: this.CaptchaResponse,
			SchoolYear: this.SchoolYear
        };

        this.appSvc.SaveNominationFormPublic(packet).pipe(takeUntil(this._subrelease)).subscribe(res => {
            this.splashSvc.CloseSplash();
            this.splashSvc.SnackbarSuccess(`Submit successful`, 10000);
        }, error => {
            this.splashSvc.CloseSplash();
            this.splashSvc.SnackbarError(error.error, 10000);
            this.AlreadySubmitted = false;
        });
    }

    captcha(captchaResponse: string): void {
        this.CaptchaResponse = captchaResponse;;
    }

    ngOnDestroy() {
        this._subrelease.next();
        this._subrelease.complete();
    }
}
